import React from 'react';
import { Modal, Button, Container, Row, Col, Form, Alert, InputGroup, FormControl, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classes from './SaveSearchModal.module.css';
import { renderTooltipOverlay, searchFieldLabels, searchField, updateObject, API_URLS, GLOBAL_DATE_FORMAT, BOOTSTRAP_CLASSES } from '../../../shared/utility';
import { ReactComponent as InfoCircleLightIcon } from '../../../assets/infoCircleLight.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18next';
import moment from 'moment';
import { Formik } from 'formik';
import * as Yup from "yup";
import graphQLClientInstance from '../../../axios-graphql-client';
import { FONTAWESOME_ICON_NAMES } from '../../../fontawesome';

const SaveSearchModal = (props: any) => {
  const { t } = useTranslation();
  const [openSummary, setOpenSummary] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  return (
    <Modal
      centered
      show={props.show}
      onHide={() => {
        setErrorMessage("");
        props.setShow(false);
      }}>
      <Formik
        initialValues = {{
          searchName: '',
          isDefault: false
        }}
        validationSchema = {
          Yup.object().shape({
            searchName: Yup.string()
              .required(t("validation_errors.required"))
            })
          }
        onSubmit = {(values: any, { setFieldValue, setSubmitting }) => {
          const newSavedSearch = {
            "searchName": values.searchName,
            "isDefault": values.isDefault,
            "searchTerms": props.input
          };
          setSubmitting(true);
          graphQLClientInstance.post(API_URLS.SEARCHES, newSavedSearch).then(res => {
            props.onAddSavedSearch(updateObject(newSavedSearch, {datatype: res.data.datatype}));
            props.setShow(false);
          }).catch(err => {
            if (err.response && err.response.data && err.response.data.message) {
              setErrorMessage(err.response.data.message);
            }
          }).finally(() => setSubmitting(false));
        }}>
      {formProps => (
        <Form
          onSubmit={(e) => {
            formProps.handleSubmit();
            e.preventDefault();
          }}>
          <Modal.Header className={classes.header}>
            <Modal.Title>{t("save_search")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className={classes.body}>
            <Container>
              {errorMessage?
                <Alert key="savedSearchError" variant="danger">
                  {t(errorMessage)}
                </Alert>
              :<></>}
              <Row>
              <Col md="7">
                <Form.Group controlId="searchName">
                  <Form.Label className={["w-100", classes.required].join(" ")}>
                    {t("search_name")}
                    {renderTooltipOverlay(
                      t("tooltips_save_search.search_name"),
                    <InfoCircleLightIcon />)}
                  </Form.Label>
                  <InputGroup className="mb-3">
                    <FormControl
                      name='searchName'
                      as="input"
                      type="text"
                      className={["shadow-none", formProps.errors.searchName?BOOTSTRAP_CLASSES.IS_INVALID:""].join(" ")}
                      size="sm"
                      value={formProps.values.searchName}
                      onChange={value => formProps.handleChange(value)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formProps.errors.searchName}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md="5">
                <Form.Group controlId="isDefault">
                  <Form.Label className={["w-100", classes.emptyLabel].join(" ")}/>
                  <Form.Check
                    className={["mb-3", classes.formCheck].join(" ")}
                    type="checkbox"
                    name="isDefault"
                    label="Default search"
                    checked={formProps.values.isDefault}
                    onChange={(evt: React.SyntheticEvent) => formProps.setFieldValue("isDefault", (evt.target as HTMLInputElement).checked)}
                  />
                </Form.Group>
              </Col>
              <Col md="12">
                <Container className={classes.region}>
                  <Row className={classes.regionHeader}>
                    <Col xs="11" className={classes.title}>{t("summary")}</Col>
                    <Col xs="1" className={classes.collapseButton}>
                      <Button
                        onClick={() => setOpenSummary(!openSummary)}
                        className={openSummary?"open":""}
                        style={{'padding': '0 5px', 'background': '#E8E8E8', 'borderBottomLeftRadius': '0px', 'borderBottomRightRadius': '0px'}}
                        variant="link">
                          <FontAwesomeIcon icon={['fas', FONTAWESOME_ICON_NAMES.CHEVRON_UP]} className={classes.paginationChevron} />
                      </Button>
                    </Col>
                  </Row>
                  <Collapse in={openSummary}>
                    <Row className={["no-gutters", classes.summaryRow].join(" ")} style={{marginLeft:'-15px', marginRight: '-15px'}}>
                      {Object.keys(props.input).map((inputKey: any, index: number) => (
                        <Col xs="12" key={`input-key-${index}`}>
                          <span className={classes.searchKey}>
                            {t(searchFieldLabels[inputKey]?.translation)}{': '}
                          </span>
                          <span className={classes.searchValue}>
                            {parseSearchValue(inputKey, props.input[inputKey]['name'], props.input[inputKey]['nameSelect'])}
                          </span>
                        </Col>
                      ))}
                    </Row>
                  </Collapse>
                </Container>
              </Col>
            </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-primary"
              onClick={() => {
                setErrorMessage("");
                props.setShow(false);
              }}>
                {t("dismiss")}
            </Button>
            <Button id="submitSaveSearch" disabled={formProps.isSubmitting} variant="primary" type="submit" className="float-right">{t("save_search")}</Button>
          </Modal.Footer>
        </Form>
      )}
      </Formik>
    </Modal>
  );
};

const parseSearchValue = (key: string, value: any, valueSelect = '') => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "N/A";
    }
    if ([
          searchField.APPLICATION_DATE,
          searchField.APPLICATION_PUB_DATE,
          searchField.GRANT_REGISTRATION_DATE,
          searchField.GRANT_REGISTRATION_PUB_DATE,
          searchField.RENEWAL_DATE,
          searchField.VARIETY_INSERT_DATE,
          searchField.DENOMINATION_INSERT_DATE
        ].includes(key)) {
      const dateArray = value.filter((el: any) => el.value).map(function(el: any) {
          return `${el.type}: ${moment(el.value, 'YYYYMMDD', true).format(GLOBAL_DATE_FORMAT)}`;
      });
      return dateArray.join(" ");
    } else if ([
                searchField.APPLICATION_NUMBER,
                searchField.BREEDERS_NAME,
                searchField.PARTIES,
                searchField.GRANT_REGISTRATION_NUMBER
              ].includes(key)) {
        // singleTypeAhead
      return `${i18n.t(valueSelect)}: ${value[0].value}`;
    } else if (key === searchField.COUNTRIES) {
        return value.filter(Boolean).map((el: any) => JSON.parse(el.value).countryid).join(", ");
    } else if ([
                searchField.DENOMINATION_NATURE,
                searchField.VARIETY_STATUS,
                searchField.REGISTER_TYPE,
                searchField.SPECIES_CROP_SECTOR,
                searchField.DENOMINATION_STATUS
              ].includes(key)) {
      return value.filter(Boolean).map((el: any) => el.label).join(", ");
    } else if ([
                searchField.SPECIES_LATIN_NAME,
                searchField.SPECIES_CLASS_CODE,
                searchField.SPECIES_CLASS_NAME
              ].includes(key)) {
      return `${i18n.t(valueSelect)}: ${value.filter(Boolean).map((el: any) => el.value).join(", ")}`;
    }
  } else {
    return value?`${i18n.t(valueSelect)}: ${value}`:"N/A";
  }
}

export default SaveSearchModal;
